'use client';
/**
 * components/MobileListView.tsx — OGRYN
 *
 * Vue liste prestataires (2 colonnes). Aucun emoji — SVG uniquement.
 * (jobs / missions / bons plans / dispos retirés — hors-scope OGRYN.)
 * ⚠️ Aucun import Leaflet.
 */

import Link from 'next/link';
import { useMobile } from '@/contexts/MobileContext';
import { trpc } from '@/lib/trpc/client';

const NAV_H = 56;
const FAB_H = 64;

// ── SVG icons ──────────────────────────────────────────────────────────────────

function IconPin() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
    </svg>
  );
}

function IconHammer() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15.5 2H8.6c-.4 0-.8.2-1.1.5-.3.3-.4.7-.5 1.1l-.6 7.8c0 .7.2 1.4.6 2 .4.6 1 1 1.6 1.1L15.5 22l6.5-9.5-4.5-2.5"/>
      <path d="M9.4 9.8 6.8 7.2"/>
    </svg>
  );
}

function IconVip() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────

export default function MobileListView({ inPanel = false }: { inPanel?: boolean }) {
  const { filters } = useMobile();

  const { data: artData, isFetching: loading } = trpc.prestataires.search.useQuery(
    { city: filters.ville || undefined, limit: 40 },
  );

  const list = artData?.prestataires ?? [];

  return (
    <div style={inPanel ? {
      /* Mode panel : remplit le conteneur flex parent */
      height: '100%',
      background: '#0f0d0b',
      overflowY: 'auto',
      paddingBottom: 10,
      paddingTop: 12,
      scrollbarWidth: 'none',
    } : {
      position: 'fixed', inset: 0, bottom: NAV_H,
      background: '#0f0d0b',
      overflowY: 'auto',
      paddingBottom: FAB_H,
      paddingTop: 12,
      zIndex: 100,
    }}>
      {/* En-tête */}
      <div style={{ padding: '0 12px 8px', borderBottom: '0.5px solid rgba(255,255,255,.06)', marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,.4)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'flex', alignItems: 'center', gap: 6 }}>
          <IconHammer />
          Prestataires
          {filters.ville && <span style={{ color: '#2E7D52', marginLeft: 4 }}>· {filters.ville}</span>}
        </div>
      </div>

      {/* Skeleton */}
      {loading && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: '0 10px' }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{ background: 'rgba(255,255,255,.04)', borderRadius: 14, padding: 12, height: 110, border: '0.5px solid rgba(255,255,255,.06)' }}>
              <div style={{ height: 12, background: 'rgba(255,255,255,.08)', borderRadius: 4, marginBottom: 8, width: '70%' }} />
              <div style={{ height: 10, background: 'rgba(255,255,255,.05)', borderRadius: 4, marginBottom: 6, width: '50%' }} />
              <div style={{ height: 10, background: 'rgba(255,255,255,.05)', borderRadius: 4, width: '60%' }} />
            </div>
          ))}
        </div>
      )}

      {/* ── PRESTATAIRES ── */}
      {!loading && (
        <>
          {list.length === 0 && <Empty label="prestataire" />}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: '0 10px' }}>
            {list.map(({ profile: a, global_score }) => {
              const name     = a.business_name ?? 'Prestataire';
              const initials = name.slice(0, 2).toUpperCase();
              const rating   = global_score ? Number(global_score).toFixed(1) : null;
              const modes    = [a.offers_home && 'Domicile', a.offers_garage && 'Garage'].filter(Boolean).join(' · ');
              return (
                <Link key={a.id} href={`/search/prestataires/${a.id}`}
                  style={{ display: 'block', textDecoration: 'none', background: 'rgba(255,255,255,.04)', borderRadius: 14, border: '0.5px solid rgba(255,255,255,.07)', padding: 12, transition: 'border-color .15s', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg,#2E7D52,#266845)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 900, fontSize: 12, flexShrink: 0 }}>
                      {initials}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,.88)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                        {name}
                      </div>
                      {modes && (
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,.35)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {modes}
                        </div>
                      )}
                    </div>
                  </div>
                  {a.city && (
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,.3)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
                      <IconPin />{a.city}
                    </div>
                  )}
                  {rating && (
                    <div style={{ fontSize: 11, color: '#F59E0B', fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
                      <IconVip />{rating}
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function Empty({ label }: { label: string }) {
  return (
    <div style={{ padding: '40px 20px', textAlign: 'center', color: 'rgba(255,255,255,.25)', fontSize: 13 }}>
      Aucun {label} trouvé
    </div>
  );
}
